import { LifecycleBadge } from '@/components/clinical/LifecycleBadge';
import { PushStatusChip } from '@/components/clinical/PushStatusChip';
import type { PushRecordView } from '@/lib/pushStatusChip';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { PatientAvatar } from '@/components/ui/PatientAvatar';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { PhaseBadge } from '@/components/ui/PhaseBadge';
import { useDemoMode, DEMO_PATIENTS, DEMO_ASSESSMENTS, DEMO_APPOINTMENTS, DEMO_STATS } from '@/contexts/DemoContext';
import { safeLower, safeSplit } from '@/lib/safeString';

// ── Types ──────────────────────────────────────────────────────────────────

interface DashboardStats {
  totalPatients: number;
  activePatients: number;
  assessmentsToday: number;
  assessmentsThisMonth: number;
  assessmentsTotal: number;
  pendingDdxReviews: number;
  activeCarePatientsCount: number;
  avgWellnessScore: number | null;
  completionRate: number | null;
  topDiagnoses: Array<{ name: string; count: number }>;
  bodyRegions: Array<{ region: string; count: number }>;
  monthlyTrend: Array<{ month: string; count: number }>;
  assessmentsByType: Array<{ type: string; count: number }>;
  assessmentsByStatus: Array<{ status: string; count: number }>;
}

interface RecentPatient {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  carePhase: string;
  lastAssessmentDate: string | null;
}

interface DraftAssessment {
  id: string;
  patientFirstName: string;
  patientLastName: string;
  clinicianFirstName: string;
  clinicianLastName: string;
  type: string;
  status: string;
  confirmedHypotheses?: number | null;
  reportVersion?: number | null;
  currentStep: number;
  draftedAt: number | null;
  finalizedAt: number | null;
  completedAt: number | null;
  createdAt: number;
  /** Run N-3 — compact PracticeHub push record (null when none). */
  phPush?: PushRecordView | null;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function age(dob: string): number {
  const today = new Date();
  const birth = new Date(dob);
  let years = today.getFullYear() - birth.getFullYear();
  if (
    today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
  ) {
    years--;
  }
  return years;
}

function greetingKey(): string {
  const h = new Date().getHours();
  if (h < 12) return 'dashboard.greeting.morning';
  if (h < 17) return 'dashboard.greeting.afternoon';
  return 'dashboard.greeting.evening';
}

function formatMonth(ym: string | null | undefined): string {
  // Fix 9 — null-safe split. Some clinics' analytics payload returns
  // a monthlyTrend row with a null `month`; the prior `ym.split('-')`
  // crashed the entire dashboard page.
  const parts = safeSplit(ym, '-');
  const y = parts[0] ?? '';
  const m = parts[1] ?? '';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthIndex = parseInt(m, 10) - 1;
  const monthLabel = monthIndex >= 0 && monthIndex < 12 ? months[monthIndex] : '';
  return `${monthLabel} ${y.slice(2)}`.trim() || '—';
}

// ── Honest per-section error/retry affordance ───────────────────────────────
// Wave 2 (B): a failed fetch must read as "something broke + retry", NOT as a
// false "empty clinic". Mirrors the error panel in AnalyticsDashboard.tsx.
function SectionError({ label, onRetry }: { label: string; onRetry: () => void }): JSX.Element {
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center space-y-2">
      <p className="font-medium text-red-700">{label}</p>
      <button
        onClick={onRetry}
        className="px-4 py-2 bg-[#2c2c2a] text-white rounded-lg text-sm font-medium hover:bg-[#444441] transition-colors"
      >
        Retry
      </button>
    </div>
  );
}

// ── Component ──────────────────────────────────────────────────────────────

export function DashboardPage(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isFrontDesk = user?.role === 'front_desk';
  const { isDemoMode } = useDemoMode();

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [patients, setPatients] = useState<RecentPatient[]>([]);
  const [drafts, setDrafts] = useState<DraftAssessment[]>([]);
  const [recentAssessments, setRecentAssessments] = useState<DraftAssessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [phConfigured, setPhConfigured] = useState<boolean | null>(null); // null = checking
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [todayAppts, setTodayAppts] = useState<Array<Record<string, unknown>>>([]);
  const [apptsLoading, setApptsLoading] = useState(false);
  const [apptsError, setApptsError] = useState<string | null>(null);
  const [apptsNotice, setApptsNotice] = useState<string | null>(null);
  // Wave 2 (B) — per-section load-error flags so one failed fetch shows an
  // honest error/retry for THAT panel only, instead of a false "empty clinic".
  const [loadErrors, setLoadErrors] = useState<{ stats: boolean; patients: boolean; assessments: boolean; drafts: boolean }>(
    { stats: false, patients: false, assessments: false, drafts: false },
  );

  // Load today's appointments from PracticeHub
  const loadTodayAppts = async (refresh = false) => {
    setApptsLoading(true);
    setApptsError(null);
    setApptsNotice(null);
    try {
      const url = refresh ? '/practicehub/appointments/today?refresh=1' : '/practicehub/appointments/today';
      const ar = await api.get<Record<string, unknown>[]>(url);
      if (!ar.success) {
        // Never surface raw "HTTP 4xx/5xx" strings to the clinician — always map
        // to an actionable friendly message.
        const raw = ar.error ?? '';
        const friendly = /429|rate|too many/i.test(raw)
          ? "Today's schedule is temporarily unavailable. It will refresh automatically."
          : /401|403|auth|invalid.*key|expired/i.test(raw)
          ? 'PracticeHub API key expired or invalid — please update it in Settings → Integrations → PracticeHub.'
          : /HTTP 4\d\d|400|bad request/i.test(raw)
          ? "Unable to load today's schedule — the PracticeHub connection may need reconfiguring. Go to Settings → Integrations → PracticeHub."
          : raw || 'Failed to load appointments';
        setApptsError(friendly);
        setTodayAppts([]);
        return;
      }
      // Rate-limited / config-error response: backend sent cached data (or [])
      // plus a human message and an errorType we can surface.
      const rateLimited = (ar as unknown as { rateLimited?: boolean }).rateLimited === true;
      const configError = (ar as unknown as { configError?: boolean }).configError === true;
      const message = (ar as unknown as { message?: string }).message ?? null;
      if (rateLimited && message) setApptsNotice(message);
      if (configError && message) setApptsError(message);

      const appts = Array.isArray(ar.data) ? ar.data : [];
      // Already sorted by backend (sort=start:asc), but ensure
      appts.sort((a, b) => String(a['starts_at'] ?? a['start'] ?? '').localeCompare(String(b['starts_at'] ?? b['start'] ?? '')));
      setTodayAppts(appts.slice(0, 30));
    } catch (e: unknown) {
      const raw = e instanceof Error ? e.message : 'Failed to load appointments';
      // Graceful rate-limit text even if thrown (shouldn't happen with new backend)
      const friendly = /429|rate|too many/i.test(raw)
        ? "Today's schedule is temporarily unavailable. It will refresh automatically."
        : raw;
      setApptsError(friendly);
      setTodayAppts([]);
    } finally {
      setApptsLoading(false);
    }
  };

  // Check PracticeHub status + load today's appointments
  useEffect(() => {
    api.get<{ configured: boolean }>('/practicehub/status').then(r => {
      if (r.success && r.data?.configured) {
        setPhConfigured(true);
        void loadTodayAppts();
      } else {
        setPhConfigured(false);
      }
    }).catch(() => { setPhConfigured(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await api.post<{ created: number; updated: number; total: number }>('/practicehub/sync', {});
      const d = res.data;
      if (d && d.total > 0) {
        setSyncResult({ message: `${d.created} new, ${d.updated} updated`, type: 'success' });
      } else {
        setSyncResult({ message: 'Up to date', type: 'success' });
      }
      // Refresh patient list
      const pRes = await api.get<{ items: RecentPatient[] }>('/patients?limit=6&sort=createdAt');
      if (pRes.success && pRes.data) setPatients((pRes.data as { items: RecentPatient[] }).items ?? []);
    } catch {
      setSyncResult({ message: 'Sync failed', type: 'error' });
    }
    setSyncing(false);
    setTimeout(() => setSyncResult(null), 4000);
  };

  // Wave 2 (B) — Promise.allSettled so each panel degrades INDEPENDENTLY: a
  // fulfilled call runs its setter exactly as before; a rejected call (or a
  // non-success payload) flags ONLY its section, which then shows an honest
  // error/retry while the other panels still render their real data. A 200 with
  // 0 items remains a genuine empty-state (no error flag). `load` is reusable so
  // the per-section Retry buttons can re-run it.
  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setLoadErrors({ stats: false, patients: false, assessments: false, drafts: false });

    const tasks: Promise<unknown>[] = [
      api.get<DashboardStats>('/analytics/dashboard'),
      api.get<{ items: RecentPatient[] }>('/patients?limit=6&sort=createdAt'),
      api.get<DraftAssessment[]>('/assessments?view=recent-completed&limit=6'),
    ];
    if (isFrontDesk) {
      tasks.push(api.get<DraftAssessment[]>('/assessments?status=clinician_draft'));
    }

    const results = await Promise.allSettled(tasks);

    const r0 = results[0];
    if (r0.status === 'fulfilled' && (r0.value as { success: boolean }).success) {
      const data = (r0.value as { data?: DashboardStats }).data;
      if (data) setStats(data);
    } else {
      setLoadErrors((e) => ({ ...e, stats: true }));
    }

    const r1 = results[1];
    if (r1.status === 'fulfilled' && (r1.value as { success: boolean }).success) {
      const data = (r1.value as { data?: { items: RecentPatient[] } }).data;
      if (data) setPatients(data.items);
    } else {
      setLoadErrors((e) => ({ ...e, patients: true }));
    }

    const r2 = results[2];
    if (r2.status === 'fulfilled' && (r2.value as { success: boolean }).success) {
      const data = (r2.value as { data?: DraftAssessment[] }).data;
      // Server-side filtered/ordered/limited (view=recent-completed).
      setRecentAssessments(Array.isArray(data) ? data : []);
    } else {
      setLoadErrors((e) => ({ ...e, assessments: true }));
    }

    if (isFrontDesk) {
      const r3 = results[3];
      if (r3 && r3.status === 'fulfilled' && (r3.value as { success: boolean }).success) {
        const data = (r3.value as { data?: DraftAssessment[] }).data;
        const items = Array.isArray(data) ? data : [];
        setDrafts(items);
      } else {
        setLoadErrors((e) => ({ ...e, drafts: true }));
      }
    }

    setLoading(false);
  }, [isFrontDesk]);

  useEffect(() => { if (isDemoMode) return; void load(); }, [load, isDemoMode]);

  // ── Demo mode override ──
  useEffect(() => {
    if (isDemoMode) {
      setStats(DEMO_STATS as unknown as DashboardStats);
      setPatients(DEMO_PATIENTS.slice(0, 6) as unknown as RecentPatient[]);
      setRecentAssessments(DEMO_ASSESSMENTS as unknown as DraftAssessment[]);
      setTodayAppts(DEMO_APPOINTMENTS as unknown as Record<string, unknown>[]);
      setLoading(false);
    }
  }, [isDemoMode]);

  // ── Derived data ───────────────────────────────────────────────────────
  const clinicianName = user ? [user.title, user.firstName].filter(Boolean).join(' ') : 'Doctor';
  const formattedDate = new Date().toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  const wellnessScore = stats?.avgWellnessScore != null ? Math.round(stats.avgWellnessScore) : null;
  const completionRate = stats?.completionRate != null ? Math.round(stats.completionRate) : null;

  // Chart data: last 6 months
  const chartData = (stats?.monthlyTrend ?? []).slice(-6).map((d, i, arr) => ({
    month: formatMonth(d.month),
    count: d.count,
    isCurrent: i === arr.length - 1,
  }));

  // Body regions: top 5
  const bodyRegions = (stats?.bodyRegions ?? []).slice(0, 5);
  const maxRegionCount = Math.max(...bodyRegions.map(r => r.count), 1);
  const regionColors = ['#2c2c2a', '#5f5e5a', '#8a8478', '#b5ae9f', '#d6cfc4'];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">

      {/* ── Greeting + Quick Actions ──────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-medium text-gray-800">
            {t(greetingKey())}, {clinicianName}
          </h1>
          <p className="text-sm text-gray-500">{formattedDate}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!isFrontDesk && (
            <>
              <Link
                to="/patients/new"
                className="bg-[#2c2c2a] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#444441] transition-colors"
              >
                + {t('dashboard.newPatient')}
              </Link>
              <Link
                to="/assessment/new"
                className="border border-[#d6cfc4] text-[#2c2c2a] px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#f5f5f0] transition-colors"
              >
                {t('dashboard.newAssessment')}
              </Link>
            </>
          )}
          <Link
            to="/patients"
            className="border border-[#d6cfc4] text-[#2c2c2a] px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#f5f5f0] transition-colors"
          >
            {t('dashboard.viewAllPatients')}
          </Link>
          {phConfigured && !isFrontDesk && (
            <button
              onClick={handleSync}
              disabled={syncing}
              className="border border-[#d6cfc4] text-[#2c2c2a] px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#f5f5f0] transition-colors disabled:opacity-50"
            >
              {syncing ? '⟳ Syncing...' : '⟳ Sync PracticeHub'}
            </button>
          )}
        </div>
        {syncResult && (
          <div className={`text-xs px-3 py-1.5 rounded-lg mt-1 ${syncResult.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {syncResult.message}
          </div>
        )}
      </div>

      {loadErrors.stats ? (
        <SectionError label="Couldn't load clinic statistics." onRetry={() => void load()} />
      ) : (<>
      {/* ── 4 Stat Cards — responsive: 4 cols desktop, 2 cols tablet/phone ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }} className="stat-grid">
        {[
          { label: t('dashboard.totalPatients'), value: loading ? '—' : String(stats?.totalPatients ?? 0), sub: !loading && (stats?.activePatients ?? 0) > 0 ? `${stats?.activePatients} active` : '' },
          { label: t('dashboard.thisMonth'), value: loading ? '—' : String(stats?.assessmentsThisMonth ?? 0), sub: !loading ? t('dashboard.assessments') : '' },
          { label: t('dashboard.avgWellness'), value: loading ? '—' : wellnessScore != null ? `${wellnessScore}/100` : '—', sub: t('dashboard.stable') },
          { label: t('dashboard.completion'), value: loading ? '—' : completionRate != null ? `${completionRate}%` : '—', sub: !loading && completionRate != null ? t('dashboard.onTrack') : '' },
        ].map((card, i) => (
          <div key={i} style={{
            background: '#fff', border: '0.5px solid #e8e3da', borderRadius: '10px',
            padding: '16px', overflow: 'hidden', minWidth: 0,
          }}>
            <div style={{ fontSize: '11px', color: '#a39d94', textTransform: 'uppercase' as const, letterSpacing: '0.5px', marginBottom: '6px', whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {card.label}
            </div>
            <div style={{ fontSize: 'clamp(22px, 4vw, 28px)', fontWeight: 500, color: '#2c2c2a', lineHeight: 1.1, whiteSpace: 'nowrap' as const }}>
              {card.value}
            </div>
            {card.sub && (
              <div style={{ fontSize: '11px', color: '#a39d94', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                {card.sub}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Charts Section (2fr + 1fr) ───────────────────────────────── */}
      {!loading && stats && (
        <div className="grid grid-cols-[2fr_1fr] gap-3">
          {/* Assessment Volume — Bar Chart */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-baseline justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-800">{t('dashboard.assessmentsPerMonth')}</h3>
              <span className="text-[11px] text-gray-400">{t('dashboard.last6Months')}</span>
            </div>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData} margin={{ left: -10, right: 10 }}>
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                    cursor={{ fill: '#f9fafb' }}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]} name="Assessments">
                    {chartData.map((entry, i) => (
                      <Cell key={i} fill={entry.isCurrent ? '#2c2c2a' : '#e5e2dc'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-gray-400 py-10 text-center">{t('dashboard.noDataYet')}</p>
            )}
          </div>

          {/* Top Body Regions — Horizontal bars */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-800 mb-4">{t('dashboard.bodyRegionDistribution')}</h3>
            {bodyRegions.length > 0 ? (
              <div className="space-y-3">
                {bodyRegions.map((r, i) => (
                  // Fix 9 — fallback key so a null region doesn't
                  // collapse multiple rows onto the same React key.
                  <div key={r.region ?? `untagged-${i}`} className="flex items-center gap-3">
                    {/* Fix 9 — `r.region` is typed `string` but the
                        analytics endpoint can return `null` for rows
                        whose body region wasn't tagged. Guard via
                        `safeLower` so the dashboard doesn't crash
                        the whole route on a single null cell. */}
                    <span className="text-[12px] text-gray-600 w-[80px] truncate shrink-0">{t(`bodyRegions.${safeLower(r.region)}`, r.region ?? '—')}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${(r.count / maxRegionCount) * 100}%`,
                          backgroundColor: regionColors[i] || '#b3cde8',
                        }}
                      />
                    </div>
                    <span className="text-[11px] text-gray-500 w-[28px] text-right tabular-nums">{r.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 py-10 text-center">{t('dashboard.noRegionData')}</p>
            )}
          </div>
        </div>
      )}
      </>)}

      {/* ── Front Desk — Needs Completion ─────────────────────────────── */}
      {isFrontDesk && (
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-gray-800">{t('dashboard.needsCompletion')}</h2>
              {!loading && drafts.length > 0 && (
                <span className="inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1.5 rounded-full bg-orange-500 text-white text-xs font-bold">
                  {drafts.length}
                </span>
              )}
            </div>
          </div>
          {loading ? (
            <div className="space-y-3">
              {[1, 2].map(i => (
                <div key={i} className="h-20 rounded-lg bg-gray-100 animate-pulse" />
              ))}
            </div>
          ) : loadErrors.drafts ? (
            <SectionError label="Couldn't load drafts awaiting completion." onRetry={() => void load()} />
          ) : drafts.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center">
              <p className="text-sm text-gray-400">{t('dashboard.allCaughtUp')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {drafts.map((d) => (
                // FIX-17 — real <Link> instead of fake onClick on a <div>.
                // Drafts now support middle-click / cmd-click to open in
                // a new tab, and cursor + keyboard work like any link.
                // Dropped the erroneous POST /start-front-desk call that
                // was firing on EVERY draft click regardless of role.
                <Link
                  key={d.id}
                  to={`/assessment/${d.id}`}
                  className="block rounded-xl border border-orange-200 bg-orange-50/50 p-4 hover:bg-orange-50 transition-colors"
                  aria-label={`Resume draft assessment for ${d.patientFirstName} ${d.patientLastName}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <PatientAvatar name={`${d.patientFirstName} ${d.patientLastName}`} size={36} />
                      <div>
                        <p className="font-medium text-gray-800">{d.patientFirstName} {d.patientLastName}</p>
                        <p className="text-xs text-gray-500">
                          {t('dashboard.byDoctor')}{d.clinicianFirstName} {d.clinicianLastName}
                          {d.draftedAt && (
                            <> · {t('dashboard.saved')} {new Date(d.draftedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Sprint 1b-3 — a confirmed-but-not-issued record looks
                          exactly like an untouched draft without this. */}
                      <LifecycleBadge assessment={d} />
                      <StatusBadge status="clinician_draft" t={t} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ── Tables Section (2 equal columns) ─────────────────────────── */}
      <div className="grid grid-cols-2 gap-3">

        {/* Recent Patients */}
        <section className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between flex-wrap gap-1 px-5 py-3.5 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-800">{t('dashboard.recentPatients')}</h2>
            <Link to="/patients" className="text-[12px] font-medium text-charcoal-600 hover:text-charcoal-900">
              {t('common.viewAll')} →
            </Link>
          </div>

          {loading ? (
            <div className="p-5 space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-10 rounded bg-gray-100 animate-pulse" />
              ))}
            </div>
          ) : loadErrors.patients ? (
            <div className="p-5">
              <SectionError label="Couldn't load recent patients." onRetry={() => void load()} />
            </div>
          ) : patients.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-sm font-medium text-gray-700">{t('dashboard.noPatients')}</p>
              <p className="text-xs text-gray-400 mt-1">{t('dashboard.addFirstPatientMsg')}</p>
              <Link
                to="/patients/new"
                className="inline-flex items-center mt-3 px-4 py-2 bg-[#2c2c2a] text-white rounded-lg text-sm font-medium hover:bg-[#444441] transition-colors"
              >
                {t('dashboard.addFirstPatient')}
              </Link>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-5 py-2.5 text-[11px] uppercase tracking-wide text-gray-400 font-medium">{t('dashboard.table.patient')}</th>
                  <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wide text-gray-400 font-medium hidden sm:table-cell">{t('dashboard.table.age')}</th>
                  <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wide text-gray-400 font-medium">{t('dashboard.table.carePhase')}</th>
                  <th className="px-3 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {patients.map((p) => (
                  <tr key={p.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <PatientAvatar name={`${p.firstName} ${p.lastName}`} />
                        <span className="text-[13px] font-medium text-gray-800">{p.firstName} {p.lastName}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-[13px] text-gray-500 hidden sm:table-cell">
                      {p.dateOfBirth && new Date(p.dateOfBirth).getFullYear() > 1920 ? `${age(p.dateOfBirth)} y` : '—'}
                    </td>
                    <td className="px-3 py-2.5">
                      <PhaseBadge phase={p.carePhase} t={t} />
                    </td>
                    <td className="px-3 py-2.5">
                      <Link to={`/patients/${p.id}`} className="text-[12px] font-medium text-charcoal-600 hover:text-charcoal-900">
                        {t('common.view')} →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Recent Assessments */}
        <section className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between flex-wrap gap-1 px-5 py-3.5 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-800">{t('dashboard.recentAssessments')}</h2>
            <Link to="/patients" className="text-[12px] font-medium text-charcoal-600 hover:text-charcoal-900">
              {t('common.viewAll')} →
            </Link>
          </div>

          {loading ? (
            <div className="p-5 space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-10 rounded bg-gray-100 animate-pulse" />
              ))}
            </div>
          ) : loadErrors.assessments ? (
            <div className="p-5">
              <SectionError label="Couldn't load recent assessments." onRetry={() => void load()} />
            </div>
          ) : recentAssessments.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-sm text-gray-400">{t('dashboard.noDataYet')}</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-5 py-2.5 text-[11px] uppercase tracking-wide text-gray-400 font-medium">{t('dashboard.table.patient')}</th>
                  <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wide text-gray-400 font-medium">{t('dashboard.table.clinician', 'Clinician')}</th>
                  <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wide text-gray-400 font-medium hidden md:table-cell whitespace-nowrap">{t('dashboard.table.date')}</th>
                  <th className="px-3 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {recentAssessments.map((a) => (
                  <tr key={a.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <PatientAvatar name={`${a.patientFirstName} ${a.patientLastName}`} />
                        <span className="text-[13px] font-medium text-gray-800">{a.patientFirstName} {a.patientLastName}</span>
                        {/* Sprint 1b-3 — issued vN, so an amended report is not
                            mistaken for its superseded first version. */}
                        <LifecycleBadge assessment={a} />
                        {/* Run N-3 — delivered / retrying at hh:mm / failed after N (Push now) */}
                        <PushStatusChip
                          record={a.phPush}
                          assessmentId={a.id}
                          onChanged={(rec) => setRecentAssessments((prev) => prev.map((x) => (x.id === a.id ? { ...x, phPush: rec } : x)))}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-[12px] text-gray-600">
                      {a.clinicianFirstName} {a.clinicianLastName}
                    </td>
                    <td className="px-3 py-2.5 text-[12px] text-gray-500 hidden md:table-cell">
                      <span className="whitespace-nowrap hidden lg:inline">{new Date(a.finalizedAt ?? a.completedAt ?? a.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      <span className="whitespace-nowrap lg:hidden">{new Date(a.finalizedAt ?? a.completedAt ?? a.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <Link to={`/assessment/${a.id}`} className="text-[12px] font-medium text-charcoal-600 hover:text-charcoal-900">
                        {t('common.view')} →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

      </div>

      {/* Today's Schedule (from PracticeHub) */}
      <section className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between flex-wrap gap-2 px-5 py-3.5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-gray-800">Today&apos;s Schedule</h2>
            {phConfigured && !apptsLoading && (
              <span className="text-[10px] text-gray-400">{todayAppts.length} appointment{todayAppts.length !== 1 ? 's' : ''}</span>
            )}
          </div>
          {phConfigured && (
            <button
              onClick={() => void loadTodayAppts(true)}
              disabled={apptsLoading}
              className="text-[11px] text-teal-700 hover:text-teal-900 font-medium disabled:opacity-50 flex items-center gap-1"
            >
              <svg className={`w-3 h-3 ${apptsLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              Refresh
            </button>
          )}
        </div>

        {phConfigured === false ? (
          <div className="p-8 text-center">
            <p className="text-sm text-gray-500 mb-2">Connect PracticeHub to see today&apos;s appointments</p>
            <button
              onClick={() => navigate('/settings/clinic')}
              className="text-xs text-teal-700 hover:text-teal-900 font-medium underline"
            >
              Settings → Integrations → PracticeHub
            </button>
          </div>
        ) : apptsLoading ? (
          <div className="p-5 space-y-3">
            {[1, 2, 3].map(i => <div key={i} className="h-10 rounded bg-gray-100 animate-pulse" />)}
          </div>
        ) : apptsError ? (
          <div className="p-6 text-center">
            <p className="text-sm text-red-600 mb-2">{apptsError}</p>
            <button
              onClick={() => void loadTodayAppts(true)}
              className="text-xs text-teal-700 hover:text-teal-900 font-medium underline"
            >
              Try again
            </button>
          </div>
        ) : todayAppts.length === 0 ? (
          <div className="p-8 text-center">
            {apptsNotice ? (
              <p className="text-sm text-amber-600">{apptsNotice}</p>
            ) : (
              <p className="text-sm text-gray-500">No appointments scheduled for today.</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            {apptsNotice && (
              <div className="px-5 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-800">
                {apptsNotice}
              </div>
            )}
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-5 py-2.5 text-[11px] uppercase tracking-wide text-gray-400 font-medium">Time</th>
                  <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wide text-gray-400 font-medium">Patient</th>
                  <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wide text-gray-400 font-medium hidden sm:table-cell">Type</th>
                  <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wide text-gray-400 font-medium hidden md:table-cell whitespace-nowrap">Status</th>
                </tr>
              </thead>
              <tbody>
                {todayAppts.map((a, i) => {
                  const startStr = String(a['starts_at'] ?? a['start'] ?? '');
                  // PH returns "2026-04-13 09:30:00" format — parse carefully
                  let time = '—';
                  if (startStr) {
                    const parsed = new Date(startStr.replace(' ', 'T'));
                    if (!isNaN(parsed.getTime())) {
                      time = parsed.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                    } else {
                      // Fallback: extract HH:MM from string
                      const m = startStr.match(/(\d{2}:\d{2})/);
                      if (m) time = m[1];
                    }
                  }
                  const patientName = a['patient_name'] ?? a['patient_preferred_name'] ?? `Patient #${a['patient_id'] ?? '?'}`;
                  const apptType = a['appointment_type_name'] ?? '—';
                  const status = String(a['status'] ?? 'pending');
                  const statusLabel = status === 'processed' ? 'completed' : status;
                  return (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-2.5 text-xs font-medium text-gray-800 whitespace-nowrap">{time}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-700">{String(patientName)}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-500 hidden sm:table-cell">{String(apptType)}</td>
                      <td className="px-3 py-2.5 hidden md:table-cell">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium capitalize ${
                          status === 'arrived' ? 'bg-green-100 text-green-700' :
                          status === 'processed' ? 'bg-blue-100 text-blue-700' :
                          status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          status === 'missed' ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>{statusLabel}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
